import { ColumnDef } from '@tanstack/react-table'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AdSupplierUser } from '@magnetar/data-access'
import { adSupplierUsersService } from '@/services/ad_supplier_users_service'
import { AdSupplier, User } from '@magnetar/admin-api-interfaces'
import { useAuth } from '@/hooks/useAuth'
import { useMe } from '@/hooks/useMe'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { useState } from 'react'
import { SupplierUserRoleDialog } from './SupplierUserRoleDialog'
import { EditAdSupplierUserDialog } from './EditAdSupplierUserDialog'
import { DeleteAlert } from '@/components/DeleteAlert'

export const columns: ColumnDef<AdSupplierUser & { user: User; supplier: AdSupplier }>[] = [
  {
    accessorKey: 'supplier_user_id',
    header: 'ID',
  },
  {
    accessorKey: 'user.account',
    header: '帳號',
  },
  {
    accessorKey: 'user.user_name',
    header: '名稱',
  },
  {
    accessorKey: 'supplier.supplier_name',
    header: '供應商名稱',
  },
  {
    accessorKey: 'is_super_admin',
    header: '超級管理員',
    cell: ({ row }) =>
      row.original.is_super_admin ? (
        <Badge variant="default">超級管理員</Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const supplierUser = row.original
      const { token } = useAuth()
      const { user: me } = useMe()
      const { hasPermission } = useMyPermissions()
      // 超級管理員只有管理端（sys_admin／組織）能指定
      const canSetSuperAdmin =
        me?.role_type === 'sys_admin' ||
        hasPermission('system_provider', AllowedAction.U) ||
        hasPermission('system_ad_supplier', AllowedAction.U) ||
        hasPermission('provider_supplier_management', AllowedAction.U)
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showRoleDialog, setShowRoleDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      const toggleSuperAdmin = async () => {
        if (!token) return
        const next = !supplierUser.is_super_admin
        const result = await adSupplierUsersService.updateAdSupplierUser(
          supplierUser.supplier_id,
          supplierUser.supplier_user_id,
          { supplier_id: supplierUser.supplier_id, user_id: supplierUser.user_id, is_super_admin: next },
          token,
        )
        if (result.success) {
          toast.success(next ? '已設為超級管理員，對這家廣告商無視角色全開' : '已取消超級管理員')
          ;(table.options.meta as { reloadData: () => void })?.reloadData()
        } else {
          toast.error(result.error?.message || '更新失敗')
        }
      }

      const handleDelete = async () => {
        if (!token) return

        const result = await adSupplierUsersService.deleteAdSupplierUser(
          supplierUser.supplier_id,
          supplierUser.supplier_user_id,
          token,
        )

        if (result.success) {
          toast.success('刪除供應商使用者成功')
          ;(table.options.meta as { reloadData: () => void })?.reloadData()
        } else {
          toast.error('刪除供應商使用者失敗')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                編輯
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowRoleDialog(true)}>
                角色設定
              </DropdownMenuItem>
              {canSetSuperAdmin && (
                <DropdownMenuItem onClick={toggleSuperAdmin}>
                  {supplierUser.is_super_admin ? '取消超級管理員' : '設為超級管理員'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-destructive">
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 編輯對話框 */}
          <EditAdSupplierUserDialog
            supplierUser={supplierUser}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={() => (table.options.meta as { reloadData: () => void })?.reloadData()}
          />

          {/* 角色設定對話框 */}
          <SupplierUserRoleDialog
            supplierUser={supplierUser}
            open={showRoleDialog}
            onOpenChange={setShowRoleDialog}
            onSuccess={() => (table.options.meta as { reloadData: () => void })?.reloadData()}
          />

          {/* 刪除確認對話框 */}
          <DeleteAlert
            open={showDeleteAlert}
            onOpenChange={setShowDeleteAlert}
            onConfirm={handleDelete}
            title="確定要刪除此供應商使用者嗎？"
            description="此操作無法復原。這將永久刪除此使用者的設定和所有相關資料。"
          />
        </>
      )
    },
  },
]
